import React, { useState, useEffect } from 'react';
import { ThumbsUp, ThumbsDown, CheckCircle2, MessageSquare } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { trackToolFeedback } from '@/shared/lib/telemetry';

export interface ToolFeedbackProps {
  toolId: string;
  toolName?: string;
  className?: string;
}

const FEEDBACK_REASONS = [
  'Incorrect or broken output',
  'Too slow or froze',
  'Missing option or format',
  'Confusing to use',
  'Other issue',
];

export function ToolFeedback({ toolId, toolName, className = '' }: ToolFeedbackProps) {
  const storageKey = `ec_feedback_${toolId}`;
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isExpandingFeedback, setIsExpandingFeedback] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [comment, setComment] = useState('');
  const [submissionMessage, setSubmissionMessage] = useState('');

  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        setHasSubmitted(true);
      }
    } catch {
      // Ignore localStorage availability issues
    }
  }, [storageKey]);

  const handlePositiveFeedback = () => {
    trackToolFeedback(toolId, { rating: 'helpful' });
    try {
      localStorage.setItem(storageKey, JSON.stringify({ rating: 'helpful', at: Date.now() }));
    } catch {
      // Ignore localStorage errors
    }
    setSubmissionMessage('Thank you for your feedback! Glad EasyUtils helped.');
    setHasSubmitted(true);
  };

  const handleNegativeClick = () => {
    setIsExpandingFeedback(true);
  };

  const handleSubmitNegative = (e: React.FormEvent) => {
    e.preventDefault();
    trackToolFeedback(toolId, {
      rating: 'unhelpful',
      reason: selectedReason || 'Unspecified',
      comment: comment.trim() || undefined,
    });
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          rating: 'unhelpful',
          reason: selectedReason,
          comment: comment.trim(),
          at: Date.now(),
        }),
      );
    } catch {
      // Ignore localStorage errors
    }
    setIsExpandingFeedback(false);
    setSubmissionMessage('Thank you for the report! We will investigate and improve this tool.');
    setHasSubmitted(true);
  };

  if (hasSubmitted) {
    if (!submissionMessage) return null;
    return (
      <div
        className={`flex items-center justify-center gap-2 py-3 px-4 text-xs text-muted-foreground bg-muted/30 rounded-lg border border-border/50 text-center ${className}`}
      >
        <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
        <span>{submissionMessage}</span>
      </div>
    );
  }

  return (
    <Card
      padding="sm"
      className={`border-border/60 bg-card/60 backdrop-blur-sm shadow-xs ${className}`}
    >
      {!isExpandingFeedback ? (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
          <div>
            <p className="text-sm font-medium text-foreground">
              Was this {toolName || 'tool'} helpful?
            </p>
            <p className="text-xs text-muted-foreground">
              Help us keep tools reliable, fast, and bug-free.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs hover:text-green-600 hover:border-green-600/40"
              onClick={handlePositiveFeedback}
              aria-label="Yes, mark this tool as helpful"
            >
              <ThumbsUp className="h-3.5 w-3.5" />
              Yes
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs hover:text-amber-600 hover:border-amber-600/40"
              onClick={handleNegativeClick}
              aria-label="No, report a problem or mark this tool as unhelpful"
            >
              <ThumbsDown className="h-3.5 w-3.5" />
              No
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmitNegative} className="space-y-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-amber-500 shrink-0" />
            <p className="text-sm font-medium text-foreground">
              What could be improved with this tool?
            </p>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {FEEDBACK_REASONS.map((reason) => {
              const isSelected = selectedReason === reason;
              return (
                <button
                  key={reason}
                  type="button"
                  onClick={() => setSelectedReason(isSelected ? '' : reason)}
                  className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                    isSelected
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {reason}
                </button>
              );
            })}
          </div>

          <div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Optional details (e.g. file format, expected vs actual behavior)..."
              rows={2}
              maxLength={400}
              className="w-full text-xs p-2.5 rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => setIsExpandingFeedback(false)}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" className="text-xs">
              Submit Feedback
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
